import { CdkStep, StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, input, OnDestroy, OnInit, output, inject } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { IceStepperComponent } from '@app/shared/components/molecules/ice-stepper/ice-stepper.component';
import { CheckImeiStepComponent } from './check-imei-step/check-imei-step.component';
import { OSAffectedControlType } from '../../../models/trade-in-enums';
import { ConditionStepComponent, isPhoneDamagedOptions } from './condition-step/condition-step.component';
import { SanityInformationKeys } from '@app/support/models/sanity.model';
import { SummaryRow, SummaryStepComponent } from './summary-step/summary-step.component';
import { EquipmentServiceV2 } from '@app/sales/services/equipment-v2.service';
import { Subscription, take, tap } from 'rxjs';
import { DamageReason } from '../../../models/equipment-api.model';
import { AssessmentStepComponent } from './assessment-step/assessment-step.component';
import { InsuranceStepComponent } from './insurance-step/insurance-step.component';
import { TermsStepComponent } from './terms-step/terms-step.component';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { NgTemplateOutlet } from '@angular/common';
import { select } from '@ngxs/store';
import { MobilBytteSelectors } from '../../../states/mobilbytte/mobilbytte.selectors';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';

export interface RegisterMobilbytteFormModel {
  checkIMEIForm: FormGroup<{
    isIMEIMatching: FormControl<boolean>;
  }>;
  conditionForm: FormGroup<{
    isPhoneDamaged: FormControl<isPhoneDamagedOptions>;
    damageReasons: FormArray<FormControl<string>>;
    dateOfDamage: FormControl<string>;
    explanationOfDamage: FormControl<string>;
    confirmationOfExternalDamage: FormControl<boolean>;
  }>;
  assessmentInsuranceForm: FormGroup<{
    isCustomerInformed: FormControl<boolean>;
    isCustomerUsingInsurance: FormControl<boolean>;
  }>;
  termsForm: FormGroup<{
    hasCustomerAcceptedTerms: FormControl<boolean>;
  }>;
}

@Component({
  selector: 'app-register-mobilbytte-stepper',
  imports: [
    IceStepperComponent,
    CdkStep,
    FormsModule,
    ReactiveFormsModule,
    CheckImeiStepComponent,
    ConditionStepComponent,
    SummaryStepComponent,
    AssessmentStepComponent,
    InsuranceStepComponent,
    TermsStepComponent,
    IceButtonComponent,
    NgTemplateOutlet,
    SpinnerComponent,
  ],
  templateUrl: './register-mobilbytte-stepper.component.html',
  styleUrl: './register-mobilbytte-stepper.component.scss',
})
export class RegisterMobilbytteStepperComponent implements OnInit, OnDestroy {
  private equipmentServiceV2 = inject(EquipmentServiceV2);

  OSaffectedControlType = OSAffectedControlType;
  SanityInformationKeys = SanityInformationKeys;
  Array = Array;

  isLoadingMobilBytteBytteRegistration = select(MobilBytteSelectors.isLoadingMobilBytteBytteRegistration);

  hasInsurance = input<boolean>(false);

  infoClicked = output<OSAffectedControlType | string>();
  submitForm = output<FormGroup<RegisterMobilbytteFormModel>>();
  disclosureToFetch = output<string>();
  stepChanged = output<number>();

  registerMobilbytteForm: FormGroup<RegisterMobilbytteFormModel>;
  interactedSteps: Set<number> = new Set();
  damageReasons: DamageReason[] = [];

  subscription: Subscription;

  ngOnInit(): void {
    this.registerMobilbytteForm = this.buildForm();
    this.interactedSteps.add(0);

    if (this.hasInsurance()) {
      this.subscription = this.conditionForm.controls.isPhoneDamaged.valueChanges.subscribe(value => {
        const isCustomerInformedControl = this.assessmentInsuranceForm.controls.isCustomerInformed;
        const isCustomerUsingInsuranceControl = this.assessmentInsuranceForm.controls.isCustomerUsingInsurance;

        if (value === isPhoneDamagedOptions.YES) {
          isCustomerInformedControl.disable();
          isCustomerUsingInsuranceControl.enable();
        } else {
          isCustomerInformedControl.enable();
          isCustomerUsingInsuranceControl.disable();
        }
      });
    }

    this.equipmentServiceV2
      .getDamageReasons()
      .pipe(
        take(1),
        tap((reasons: DamageReason[]) => {
          if (reasons?.length > 0) {
            this.damageReasons = reasons;
          }
        })
      )
      .subscribe();
  }

  get checkIMEIForm(): FormGroup {
    return this.registerMobilbytteForm.get('checkIMEIForm') as FormGroup;
  }

  get conditionForm(): FormGroup {
    return this.registerMobilbytteForm.get('conditionForm') as FormGroup;
  }
  get isPhoneDamaged(): boolean {
    return this.conditionForm?.controls.isPhoneDamaged.value === isPhoneDamagedOptions.YES;
  }
  get summaryRows(): SummaryRow[] {
    return this.conditionForm?.controls.damageReasons.value.map((code: string) => {
      const reason = this.damageReasons.find(r => r.code === code);
      return { damageReason: reason?.description || '', hasDamage: true };
    });
  }

  get assessmentInsuranceForm(): FormGroup {
    return this.registerMobilbytteForm.get('assessmentInsuranceForm') as FormGroup;
  }

  get termsForm(): FormGroup {
    return this.registerMobilbytteForm.get('termsForm') as FormGroup;
  }

  lastInfoKey: { [stepIndex: number]: string | null | OSAffectedControlType.Imei } = {};
  selectedIndex = 0;

  onStepChange(event: StepperSelectionEvent) {
    this.stepChanged.emit(event.selectedIndex);
    this.markStepControlsAsTouched(event.previouslySelectedIndex);

    this.selectedIndex = event.selectedIndex;
    this.infoClicked.emit(this.lastInfoKey[this.selectedIndex] ?? null);
    //The disclosure should be fetched in the last step. If there is insurance
    //there are 5 steps, otherwise 4.
    this.disclosureToFetch.emit(this.selectedIndex === this.getStepCount() - 1 ? 'HowMobilBytteWorks' : null);
  }

  private getStepCount(): number {
    if (this.hasInsurance()) {
      return 5;
    }
    return 4;
  }

  private requiredAtLeastOneTrue(): ValidatorFn {
    return (formArray: FormArray) => {
      const hasAtLeastOneSelected = formArray.controls.some(control => control.value);
      return hasAtLeastOneSelected ? null : { atLeastOneRequiredError: true };
    };
  }

  private buildForm(): FormGroup<RegisterMobilbytteFormModel> {
    const checkIMEIForm: FormGroup = new FormGroup({
      isIMEIMatching: new FormControl(false, Validators.requiredTrue),
    }); // Step 1

    const conditionForm: FormGroup = new FormGroup({
      isPhoneDamaged: new FormControl('', Validators.required),
      damageReasons: new FormArray([], this.requiredAtLeastOneTrue()),
      dateOfDamage: new FormControl({ value: '', disabled: true }, Validators.required),
      explanationOfDamage: new FormControl({ value: '', disabled: true }, Validators.required),
      confirmationOfExternalDamage: new FormControl({ value: false, disabled: true }, Validators.requiredTrue),
    }); // Step 2

    // Step 3 uses isCustomerInformed from assessmentInsuranceForm if hasInsurance is false

    const assessmentInsuranceForm: FormGroup = new FormGroup({
      isCustomerInformed: new FormControl({ value: false, disabled: this.hasInsurance() }, Validators.requiredTrue),
      isCustomerUsingInsurance: new FormControl({ value: false, disabled: true }, Validators.requiredTrue),
    }); // Step 4

    const termsForm: FormGroup = new FormGroup({
      hasCustomerAcceptedTerms: new FormControl(false, Validators.requiredTrue),
    }); // Step 5

    return new FormGroup({
      checkIMEIForm: checkIMEIForm,
      conditionForm: conditionForm,
      assessmentInsuranceForm: assessmentInsuranceForm,
      termsForm: termsForm,
    });
  }

  private markStepControlsAsTouched(stepIndex: number): void {
    // When leaving steps, mark all its RELEVANT visible controls as touched to show errors when returning
    if (stepIndex === 0) {
      this.checkIMEIForm.markAllAsTouched();
    } else if (stepIndex === 1 && this.conditionForm.controls.isPhoneDamaged.value === isPhoneDamagedOptions.YES) {
      this.conditionForm.markAllAsTouched();
    } else if (stepIndex === 2 && !this.hasInsurance()) {
      this.assessmentInsuranceForm.controls.isCustomerInformed.markAsTouched();
    } else if (stepIndex === 3 && this.hasInsurance()) {
      if (this.conditionForm.controls.isPhoneDamaged.value === isPhoneDamagedOptions.YES) {
        this.assessmentInsuranceForm.controls.isCustomerUsingInsurance.markAsTouched();
      } else {
        this.assessmentInsuranceForm.controls.isCustomerInformed.markAsTouched();
      }
    } else if ((stepIndex === 3 && !this.hasInsurance()) || stepIndex === 4) {
      this.termsForm.markAllAsTouched();
    }
  }

  ngOnDestroy(): void {
    if (!!this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
